import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { calculateDistance } from './utils';
import PoiCardList from './poicardlist';
import TinyPoiCardList from './TinyPoiCardList';
import config from '../config';
import useTranslation from './useTranslation';
import LocalIcon from './LocalIcon';


const Icon = ({ name, className, style }) => (
  <LocalIcon icon={name} className={className} style={style} />
);

const ListPanel = ({ 
  isOpen, 
  onClose, 
  pois, 
  taxonomyData, 
  userLocation,
  selectedCategories,
  setSelectedCategories
}) => {
  const { t } = useTranslation();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCategoriesDropdownOpen, setIsCategoriesDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [sortOption, setSortOption] = useState('alphabetical');
  
  const categoriesDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const navigate = useNavigate();

  // Gestione chiusura pannello
  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
      onClose();
    }, 300);
  };

  // Gestione scroll body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Click outside per i dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoriesDropdownRef.current && !categoriesDropdownRef.current.contains(event.target)) {
        setIsCategoriesDropdownOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Gestione toggle categorie
  const handleCategoryToggle = (categoryId) => {
    // Creiamo un nuovo Set a partire da quello che abbiamo ricevuto
    const newSelectedCategories = new Set(selectedCategories);
    if (newSelectedCategories.has(categoryId)) {
      newSelectedCategories.delete(categoryId);
    } else {
      newSelectedCategories.add(categoryId);
    }
    // Lo ripasso indietro a MapList usando la stessa funzione
    setSelectedCategories(newSelectedCategories);
  };

  // Filtraggio e ordinamento POI
  const filteredAndSortedPois = useMemo(() => {
    // Prima filtriamo per categoria
    let result = selectedCategories.size > 0
      ? pois.filter(poi => {
          const poiCategoryId = poi.field_tipologia_poi?.[0]?.target_id?.toString();
          return selectedCategories.has(poiCategoryId);
        })
      : pois;

    // Poi ordiniamo
    if (sortOption === 'alphabetical') {
      return [...result].sort((a, b) => {
        const titleA = a.title?.[0]?.value || '';
        const titleB = b.title?.[0]?.value || '';
        return titleA.localeCompare(titleB);
      });
    } else if (sortOption === 'distance' && userLocation) {
      return [...result].sort((a, b) => {
        const distanceA = a.field_geolocalizzazione?.[0] 
          ? calculateDistance(
              userLocation.latitude, 
              userLocation.longitude, 
              a.field_geolocalizzazione[0].lat, 
              a.field_geolocalizzazione[0].lng
            )
          : Infinity;
        
        const distanceB = b.field_geolocalizzazione?.[0] 
          ? calculateDistance(
              userLocation.latitude, 
              userLocation.longitude, 
              b.field_geolocalizzazione[0].lat, 
              b.field_geolocalizzazione[0].lng
            )
          : Infinity;
        
        return distanceA - distanceB;
      });
    }

    return result;
  }, [pois, selectedCategories, sortOption, userLocation]);

  const panelClasses = [
    'side-panel',
    isOpen ? 'is-open' : '',
    isAnimating ? 'is-closing' : ''
  ].join(' ');

  return (
    <div className={panelClasses}>
      {/* Header con bottone di chiusura e icona */}
      <div className="sticky-top bg-white border-bottom">
        <div className="container d-flex justify-content-between align-items-center py-3">
          <button
            onClick={handleClose}
            aria-label={t('chiudi')}
            className="btn border rounded-circle d-flex align-items-center justify-content-center goback"
            style={{ position: 'relative', top: 0, left: 0 }}
          >
            <Icon name="close" />
          </button>
          
          {/* Icona sulla destra (come negli altri pannelli) */}
          <div className="d-flex align-items-center">
            <Icon name="list" className="ms-2" style={{ fontSize: '24px' }} />
          </div>
        </div>
      </div>

      <div className="container py-4">
        {/* Titolo */}
        <div className="mb-4">
          <h2>{t('listaPuntiInteresse')}</h2>
          <p>{t('esploraDescrizione')}</p>
        </div>

        {/* Filtri */}
        <div className="d-flex justify-content-end gap-2 mb-4">
          {/* Dropdown Ordinamento */}
          <div className="dropdown" ref={sortDropdownRef}>
            <button 
              className="btn border rounded-pill dropdown-toggle"
              type="button"
              onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
              style={{
                backgroundColor: 'var(--color-element)',
                color: 'var(--color-text-primary)'
              }}
            >
              {t('ordinamento')}
            </button>
            {isSortDropdownOpen && (
              <ul className="dropdown-menu show mt-1" style={{ 
                width: '230px', 
                transform: 'translateX(-50%)', 
                left: '10px',
                backgroundColor: 'var(--color-surface)',
                borderColor: 'var(--color-border)'
              }}>
                <li 
                  className="d-flex align-items-center p-2 rounded"
                  onClick={() => {
                    setSortOption('alphabetical');
                    setIsSortDropdownOpen(false);
                  }}
                  style={{ 
                    cursor: 'pointer',
                    color: 'var(--color-text-primary)'
                  }}
                >
                  <span className="flex-grow-1">{t('alfabetico')}</span>
                  {sortOption === 'alphabetical' && (
                    <span className="material-symbols-outlined" style={{ 
                      fontSize: '18px', 
                      color: 'var(--color-text-primary)' 
                    }}>
                      check
                    </span>
                  )}
                </li>
                <li 
                  className="d-flex align-items-center p-2 rounded"
                  onClick={() => {
                    setSortOption('distance');
                    setIsSortDropdownOpen(false);
                  }}
                  style={{ 
                    cursor: 'pointer',
                    color: 'var(--color-text-base)' 
                  }}
                >
                  <span className="flex-grow-1">{t('distanza')}</span>
                  {sortOption === 'distance' && (
                    <span className="material-symbols-outlined" style={{ 
                      fontSize: '18px', 
                      color: 'var(--color-text-base)' 
                    }}>
                      check
                    </span>
                  )}
                </li>
              </ul>
            )}
          </div>

          {/* Dropdown Categorie */}
          <div className="dropdown" ref={categoriesDropdownRef}>
            <button 
              className="btn border rounded-pill dropdown-toggle"
              type="button"
              onClick={() => setIsCategoriesDropdownOpen(!isCategoriesDropdownOpen)}
              style={{
                backgroundColor: 'var(--color-element)',
                color: 'var(--color-text-primary)'
              }}
            >
              {t('categorie')}
            </button>
            {isCategoriesDropdownOpen && (
              <ul className="dropdown-menu show category-dropdown mt-1" style={{ 
                width: '280px', 
                overflowY: 'auto', 
                right: '0', 
                left: 'auto', 
                paddingLeft: '4px',
                backgroundColor: 'var(--color-surface)',
                borderColor: 'var(--color-border)'
              }}>
                {Object.entries(taxonomyData).map(([id, data]) => (
                  <li key={id} 
                      className="d-flex align-items-center p-2 rounded"
                      onClick={() => handleCategoryToggle(id)}
                      style={{ 
                        cursor: 'pointer',
                        color: 'var(--color-text-primary)',
                        backgroundColor: selectedCategories.has(id) ? 'var(--hover-bg)' : 'transparent'
                      }}>
                    {data.icon ? (
                      <img src={data.icon} alt="" style={{ width: '24px', height: '24px', marginRight: '10px', borderRadius: '4px' }} />
                    ) : (
                      <div style={{
                        width: '24px',
                        height: '24px',
                        marginRight: '10px',
                        backgroundColor: data.field_colore?.[0]?.color || '#1E88E5',
                        borderRadius: '4px'
                      }} />
                    )}
                    <span className="flex-grow-1">{data.title}</span>
                    {selectedCategories.has(id) && (
                      <span className="material-symbols-outlined" style={{ 
                        fontSize: '18px', 
                        color: 'var(--color-text-base)' 
                      }}>
                        check
                      </span>
                    )}
                  </li>
                ))}
                {selectedCategories.size > 0 && (
                  <li 
                    className="d-flex align-items-center p-2 mt-2 text-muted"
                    style={{ 
                      cursor: 'pointer', 
                      borderTop: 'var(--border-25)'
                    }}
                    onClick={() => setSelectedCategories(new Set())}
                  >
                    {t('rimuoviFiltri')}
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>

        {/* Lista POI */}
        <section>
          <ul className="list-group">
            {filteredAndSortedPois.map((poi) => {
              const distance = userLocation && poi.field_geolocalizzazione?.[0]
                ? `${calculateDistance(
                    userLocation.latitude,
                    userLocation.longitude,
                    poi.field_geolocalizzazione[0].lat,
                    poi.field_geolocalizzazione[0].lng
                  ).toFixed(1)} km`
                : '';

              return (
                <li key={poi.nid[0].value} className="list-group-item list-group-item-divider mt-3 pt-3">
                  {config.components.listType === 'tiny' ? (
                    <TinyPoiCardList
                      poi={poi}
                      taxonomyData={taxonomyData}
                      distance={distance}
                      onClick={() => {
                        handleClose();
                        setTimeout(() => {
                          navigate(`/poi/${poi.nid[0].value}`);
                        }, 300);
                      }}
                    />
                  ) : (
                    <PoiCardList
                      poi={poi}
                      taxonomyData={taxonomyData}
                      distance={distance}
                      onClick={() => {
                        handleClose();
                        setTimeout(() => {
                          navigate(`/poi/${poi.nid[0].value}`);
                        }, 300);
                      }}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default ListPanel;